import { inject } from '@angular/core';

import { signalStoreFeature, withHooks, withMethods, withProps } from '@ngrx/signals';
import { Subscription } from 'rxjs';

import { REALTIME_TRANSPORT, RealtimeEvent } from './realtime-transport';

export type RealtimeStatus =
  /** Sem canal assinado. */
  | 'idle'
  /** Assinado e em dia com o servidor. */
  | 'live'
  /** Buraco detectado: esperando o refetch confirmar a versão autoritativa. */
  | 'resyncing';

export interface RealtimeHandlers<T = unknown> {
  /** Aplica um evento válido, já ordenado, sem repetição e sem buraco antes dele. */
  onEvent: (event: RealtimeEvent<T>) => void;

  /**
   * Faltou evento entre o último aplicado e o recebido.
   *
   * **Obrigatório de propósito.** Sem tratamento, um buraco faria o store divergir
   * do servidor em silêncio e para sempre — em comanda, item lançado que some da
   * tela. Quem implementa deve refazer o fetch do agregado e chamar
   * `resumeRealtime` com a versão que vier.
   *
   * @param versaoObservada versão do evento que revelou o buraco. Útil para log.
   */
  onGap: (versaoObservada: number) => void;
}

/**
 * Quantos eventos guardar durante o resync antes de desistir e pedir outro.
 *
 * A janela normal é o tempo de um refetch — punhado de eventos. Estourar isso
 * significa que o refetch travou; melhor descartar e pedir de novo do que crescer
 * sem limite.
 */
const LIMITE_DO_BUFFER = 100;

/**
 * Conecta o store a um canal de tempo real, resolvendo o que todo store conectado
 * resolve igual: descartar repetido, ignorar fora de ordem, detectar buraco,
 * reconciliar com o refetch e desconectar ao destruir.
 *
 * O transporte vem por injeção — o store não sabe se por trás tem WebSocket.
 *
 * ## O contrato de versão
 *
 * Todo evento carrega a versão monotônica do agregado no servidor:
 *
 * - `versão <= última aplicada` → descartado (repetido ou fora de ordem)
 * - `versão == última + 1` → aplicado
 * - `versão > última + 1` → **buraco**: entra em `resyncing`, chama `onGap` e
 *   **não avança a versão**. Avançar aqui seria adotar a versão de um evento que
 *   não foi aplicado, e o efeito dele nunca mais entraria na tela.
 *
 * Durante o `resyncing` os eventos ficam em buffer, não são aplicados: o refetch
 * traz o agregado inteiro e o buffer é reprocessado por cima da versão dele.
 *
 * @example
 * ```ts
 * export const ComandaStore = signalStore(
 *   withEntities<ComandaItem>(),
 *   withRealtime<ComandaEvento>(),
 *   withMethods((store, api = inject(ComandaApi)) => ({
 *     async abrir(id: string) {
 *       const comanda = await api.buscar(id);          // traz itens + versão
 *       aplicarComanda(store, comanda);
 *       store.connectRealtime(`comanda:${id}`, {
 *         onEvent: (ev) => aplicarEvento(store, ev),
 *         onGap: async () => {
 *           const atual = await api.buscar(id);
 *           aplicarComanda(store, atual);
 *           store.resumeRealtime(atual.versao);        // versão autoritativa
 *         },
 *       }, comanda.versao);
 *     },
 *   })),
 * );
 * ```
 */
export function withRealtime<T = unknown>() {
  return signalStoreFeature(
    withProps(() => ({
      _transport: inject(REALTIME_TRANSPORT),
    })),
    withMethods((store) => {
      let subscription: Subscription | undefined;
      let handlers: RealtimeHandlers<T> | undefined;
      let lastVersion = 0;
      let status: RealtimeStatus = 'idle';
      let buffer: RealtimeEvent<T>[] = [];

      const disconnectRealtime = (): void => {
        subscription?.unsubscribe();
        subscription = undefined;
        handlers = undefined;
        buffer = [];
        status = 'idle';
      };

      const entrarEmResync = (versaoObservada: number): void => {
        status = 'resyncing';
        buffer = [];
        handlers?.onGap(versaoObservada);
      };

      const receber = (event: RealtimeEvent<T>): void => {
        if (!handlers) {
          return;
        }

        if (status === 'resyncing') {
          if (buffer.length >= LIMITE_DO_BUFFER) {
            // Refetch não voltou a tempo. Descarta o acumulado e pede outro em vez
            // de deixar o buffer crescer sem limite.
            entrarEmResync(event.version);
            return;
          }

          buffer.push(event);
          return;
        }

        // Repetido ou fora de ordem: o servidor é a verdade e já passamos daqui.
        if (event.version <= lastVersion) {
          return;
        }

        // Buraco: não dá para remendar, e avançar a versão aqui perderia o evento
        // para sempre. Só o refetch pode dizer em que versão o agregado está.
        if (event.version > lastVersion + 1) {
          entrarEmResync(event.version);
          return;
        }

        handlers.onEvent(event);
        lastVersion = event.version;
      };

      return {
        /**
         * Assina um canal. Conectar de novo troca de canal, encerrando o anterior.
         *
         * @param fromVersion versão já conhecida pelo store, vinda do fetch.
         * **Obrigatório**: sem ela, uma reconexão reaplicaria todo o histórico do
         * canal e duplicaria item.
         */
        connectRealtime(channel: string, novosHandlers: RealtimeHandlers<T>, fromVersion: number): void {
          disconnectRealtime();

          handlers = novosHandlers;
          lastVersion = fromVersion;
          status = 'live';

          subscription = store._transport.connect<T>(channel).subscribe((event) => receber(event));
        },

        /**
         * Fixa a versão autoritativa depois de um refetch e volta a aplicar eventos.
         *
         * Os eventos que chegaram durante o resync são reprocessados em ordem por
         * cima dessa versão — os já contidos no refetch são descartados naturalmente
         * pela regra de versão, e um novo buraco dispara outro `onGap`.
         */
        resumeRealtime(version: number): void {
          lastVersion = version;
          status = 'live';

          const pendentes = buffer.slice().sort((a, b) => a.version - b.version);
          buffer = [];

          for (const event of pendentes) {
            receber(event);
          }
        },

        disconnectRealtime,

        /** Última versão aplicada. O refetch retoma daqui. */
        realtimeVersion(): number {
          return lastVersion;
        },

        /** `idle`, `live` ou `resyncing`. Útil para indicar sincronização na tela. */
        realtimeStatus(): RealtimeStatus {
          return status;
        },
      };
    }),
    withHooks({
      onDestroy(store) {
        store.disconnectRealtime();
      },
    }),
  );
}
